import EditIcon from "@mui/icons-material/Edit";
import { Tooltip } from "@mui/material";
import { SortingState } from "@tanstack/react-table";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";

import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import UserManagementTableContainer from "Common/Tables/UserManagementFiltersTable/UserManagementTableContainer";
import { app_modules } from "helpers/apps_modules_list";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { showCustomSwal } from "helpers/swalHelper";
import usePermission from "hooks/usePermission";
import Swal from "sweetalert2";
import {
  fetchUser as onfetchUser,
  updateStatusInUsers,
  UsersxportsCSV,
} from "../../../../slices/UserManagement/user/thunk";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { UserTypeConstant } from "Constants/UserTypeConstant";
import BlockIcon from '@mui/icons-material/Block';
const UserList = () => {
  document.title = "User List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: TableKeys?.USER_MANAGEMENT?.USER,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  const selectorganisation = createSelector(
    (state: any) => state.users,
    (Org: any) => ({
      usersList: Org.usersList,
      rowCount: Org.rowCount,
      loading: Org.loading,
      error: Org.error,
    })
  );

  const { usersList, rowCount, loading, error } =
    useSelector(selectorganisation);

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.user_management?.app_name,
    app_modules?.user_management?.modules?.user
  );

  // Get current logged-in user ID and type
  const currentUserFromRedux = useSelector((state: any) => state.Login.user);
  const currentUserFromLocalStorage = JSON.parse(localStorage.getItem("authUser") || "{}");
  const currentUser = currentUserFromRedux || currentUserFromLocalStorage;
  
  // Debug: Log the user object structure
  // useEffect(() => {
  //   console.log("Current user object:", currentUser);
  //   console.log("Redux user:", currentUserFromRedux);
  //   console.log("LocalStorage user:", currentUserFromLocalStorage);
  // }, [currentUser, currentUserFromRedux, currentUserFromLocalStorage]);
  
  const currentUserId = currentUser?.user_id || currentUser?.id;
  // Try multiple possible paths for user_type
  const currentUserType = 
    currentUser?.user_type || 
    currentUser?.data?.user_type || 
    currentUser?.userType ||
    currentUser?.data?.userType;
  const isCurrentUserSuperAdmin = currentUserType && UserTypeConstant.includes(currentUserType);
  console.log("isCurrentUserSuperAdmin", isCurrentUserSuperAdmin)
  // Use ref to store current user Super Admin status for use in permission function
  const isCurrentUserSuperAdminRef = useRef(isCurrentUserSuperAdmin);
  
  // Update ref when value changes
  useEffect(() => {
    isCurrentUserSuperAdminRef.current = isCurrentUserSuperAdmin;
    console.log("isCurrentUserSuperAdmin updated:", isCurrentUserSuperAdmin, "from user_type:", currentUserType);
  }, [isCurrentUserSuperAdmin, currentUserType]);

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [colValue, setColValue] = useState<any>();
  const [tableInstance, setTableInstance] = useState<any>();

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      onfetchUser({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: columnFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    columnFilters,
    colValue,
    sortField,
    sortDirection,
  ]);

  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Handle status toggle
  interface UserStatusRow {
    user_id?: number | string;
    id?: number | string;
    [key: string]: any;
  }

  const handleStatusToggle = useCallback(
    async (org: UserStatusRow) => {
      const updatedStatus = org.is_active === true ? false : true;

      // Show loading modal
      showCustomSwal({
        title: "Updating...",
        text: "Please wait while we update the user status.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const update_payload = { ...org, is_active: updatedStatus };
        await dispatch(updateStatusInUsers(update_payload)).unwrap();

        // Replace loading modal with success modal
        showCustomSwal({
          icon: "success",
          title: "Status Updated",
          text: `User ${
            updatedStatus ? "activated" : "deactivated"
          } successfully.`,
        });

        dispatch(
          onfetchUser({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: columnFilters,
          })
        );
      } catch (err: any) {
        // console.error("Update Status Error:", err);
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to update user status";

        // Replace loading modal with error modal
        showCustomSwal({
          icon: "error",
          title: "Update Failed",
          text: errorMessage,
        });
      }
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      columnFilters,
    ]
  );

  // Table columns
  const columns = useMemo(() => {
    // Define menu items configuration
    const menuItemsConfig = canUpdate ? [
      {
        icon: EditIcon,
        label: "Edit",
        permission: (user: any) => {
          // Check if the user being edited is a Super Admin
          const isUserSuperAdmin = user.user_type && UserTypeConstant.includes(user.user_type);
          
          // Get current user Super Admin status from ref (always up-to-date)
          const isCurrentUserSuperAdminValue = isCurrentUserSuperAdminRef.current;
          
          // Also try to get fresh value from localStorage as fallback
          const freshCurrentUser = JSON.parse(localStorage.getItem("authUser") || "{}");
          const freshCurrentUserType = 
            freshCurrentUser?.user_type || 
            freshCurrentUser?.data?.user_type || 
            freshCurrentUser?.userType ||
            freshCurrentUser?.data?.userType;
          const freshIsCurrentUserSuperAdmin = freshCurrentUserType && UserTypeConstant.includes(freshCurrentUserType);
          
          // Use ref value, but fallback to fresh value if ref is undefined
          const finalIsCurrentUserSuperAdmin = isCurrentUserSuperAdminValue !== undefined 
            ? isCurrentUserSuperAdminValue 
            : freshIsCurrentUserSuperAdmin;
          
          // If the user being edited is a Super Admin, only allow if current user is also a Super Admin
          if (isUserSuperAdmin) {
            return finalIsCurrentUserSuperAdmin;
          }
          
          // If the user being edited is NOT a Super Admin, allow if current user has update permission
          return true;
        },
        onClick: (user: any) => {
          navigate(`/user-form/${user.user_id}`, {
            state: { user_data: user },
          });
        },
        color: "primary",
      },
      {
        icon: BlockIcon,
        label: "Blocked",
        permission: (user: any) => {
          // Check if the user being viewed is a Super Admin
          const isUserSuperAdmin = user.user_type && UserTypeConstant.includes(user.user_type);
          
          // Get current user Super Admin status from ref (always up-to-date)
          const isCurrentUserSuperAdminValue = isCurrentUserSuperAdminRef.current;
          
          // Also try to get fresh value from localStorage as fallback
          const freshCurrentUser = JSON.parse(localStorage.getItem("authUser") || "{}");
          const freshCurrentUserType = 
            freshCurrentUser?.user_type || 
            freshCurrentUser?.data?.user_type || 
            freshCurrentUser?.userType ||
            freshCurrentUser?.data?.userType;
          const freshIsCurrentUserSuperAdmin = freshCurrentUserType && UserTypeConstant.includes(freshCurrentUserType);
          
          // Use ref value, but fallback to fresh value if ref is undefined
          const finalIsCurrentUserSuperAdmin = isCurrentUserSuperAdminValue !== undefined 
            ? isCurrentUserSuperAdminValue 
            : freshIsCurrentUserSuperAdmin;
          
          // Show BlockIcon only if:
          // 1. The user being viewed is a Super Admin
          // 2. The current user is NOT a Super Admin
          if (isUserSuperAdmin && !finalIsCurrentUserSuperAdmin) {
            return true;
          }
          
          return false;
        },
        onClick: (user: any) => {
          // Show a message that editing Super Admin accounts is restricted
          showCustomSwal({
            icon: "warning",
            title: "Access Restricted",
            text: "Only Super Admin users can edit Super Admin accounts.",
          } as any);
        },
        color: "error",
      },
    ] : [];

    // Filter menu items to check if any will be visible
    // This ensures we only show the Action column if there are visible menu items
    const hasVisibleMenuItems = menuItemsConfig.some((item) => {
      if (item.permission === undefined) return true;
      if (typeof item.permission === "function") {
        // For function-based permissions, we can't check without a row, so assume true if canUpdate is true
        return canUpdate;
      }
      return item.permission !== false;
    });

    const DynamicAction = hasVisibleMenuItems
      ? [
          {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => {
              return (
                <DynamicMenuItem
                  row={row.original}
                  menuItems={menuItemsConfig}
                />
              );
            },
          },
        ]
      : [];

    return [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "First Name",
        accessorKey: "first_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("first_name"),
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return org?.first_name || "-";
        },
      },
      {
        header: "Last Name",
        accessorKey: "last_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("last_name"),
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return org?.last_name || "-";
        },
      },
      {
        header: "User Name",
        accessorKey: "username",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("username"),
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return org?.username || "-";
        },
      },

      {
        header: "Type",
        accessorKey: "user_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("user_type"),
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return org?.user_type || "-";
        },
      },
      {
        header: "Designation",
        accessorKey: "designation",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("designation"),
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("email"),
      },
      {
        header: "Mobile No",
        accessorKey: "mobile_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("mobile_number"),
      },
      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const usersStatus = row.original;
          const isCurrentUser = usersStatus.user_id === currentUserId;
          const isDisabled = !canUpdate || isCurrentUser;
          const isSuperAdmin = UserTypeConstant.includes(usersStatus.user_type);
      
            return (
            <div
              style={{
                fontSize: "1.5rem",
                cursor: isDisabled || isSuperAdmin ? "not-allowed" : "pointer",
                opacity: isDisabled || isSuperAdmin? 0.5 : 1,
                position: "relative",
              }}
              onClick={() => {
                if (!isDisabled && !isSuperAdmin) {
                  handleStatusToggle(usersStatus);
                } else if (isCurrentUser) {
                  // Show a message explaining why it's disabled
                  showCustomSwal({
                    icon: "warning",
                    title: "Cannot Disable Own Account",
                    text: "You cannot disable your own account. Please ask another administrator to do this.",
                  });
                }
              }}
              title={
                isCurrentUser
                  ? "You cannot disable your own account"
                  : canUpdate
                  ? "Click to toggle status"
                  : "No permission to update"
              }
            >
              <i
                className={`bi ${
                  usersStatus.is_active
                    ? "bi-toggle-on text-success"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },
      {
        header: "Team",
        accessorKey: "team_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("team_name"),
        cell: ({ row }: { row: any }) => {
          const org = row.original.team;
          return org?.team_name || "-";
        },
      },
      {
        header: "Department",
        accessorKey: "department_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("department_name"),
        cell: ({ row }: { row: any }) => {
          const org = row.original.department;
          return org?.department_name || "-";
        },
      },
      {
        header: "Organisation",
        accessorKey: "organization_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN(
            "organization_name"
          ),
        cell: ({ row }: { row: any }) => {
          const org = row.original.organization;
          return org?.organization_name || "-";
        },
      },
      {
        header: "Address",
        accessorKey: "address",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => {
          const address = row.original?.address || "";
          const maxLength = 50; // Maximum characters to show before truncating

          if (!address) return "-";

          const shouldTruncate = address.length > maxLength;
          const displayText = shouldTruncate
            ? `${address.substring(0, maxLength)}...`
            : address;

          return (
            <Tooltip
              arrow
              placement="top"
              title={address}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 300,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div
                style={{
                  cursor: shouldTruncate ? "pointer" : "default",
                  lineHeight: 1.4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayText}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "City",
        accessorKey: "city",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("city"),
          cell: ({ row }: { row: any }) => (row.original.city || "-")
      },
      {
        header: "State",
        accessorKey: "state",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("state"),
          cell: ({ row }: { row: any }) => (row.original.state || "-")
      },
      {
        header: "Country",
        accessorKey: "country",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.USER_MANAGEMENT.USER.FILTER_DROPDOWN("country"),
        cell: ({ row }: { row: any }) => (row.original.country || "-")
      },
      {
        header: "Pincode",
        accessorKey: "pincode",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => (row.original.pincode || "-")
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user?.username}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user?.username}
            date={row.original.updated_at}
          />
        ),
      },
      ...DynamicAction,
    ];
  }, [handleStatusToggle, canUpdate, navigate, currentUserId, isCurrentUserSuperAdmin]);

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Users</h6>
                </Col>
                <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                  {/* Search Box */}
                  <SearchBox
                    value={searchInput}
                    onChange={(e: any) => setSearchInput(e.target.value)}
                    placeholder="Search..."
                  />

                  {/* Column Visibility + Actions */}
                  <Col xs={12} md={12} lg={8} xl={6} className="">
                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                      {/* Left side: Column Visibility */}
                      <div className="">
                        {tableInstance && (
                          <ColumnVisibilityDropdown
                            columns={tableInstance.getAllLeafColumns()}
                            excludedColumns={["action", "sr_no"]}
                          />
                        )}
                      </div>

                      <div className="hstack gap-2">
                        {canView && (
                          <ExportButton
                            onExport={async () => {
                              return await UsersxportsCSV({
                                search,
                                filters: columnFilters,
                              });
                            }}
                          />
                        )}
                      </div>
                      <div>
                        {canCreate && (
                          <ActionButton
                            label="Add User"
                            icon="bi bi-plus-circle"
                            navigateLink="/user-form"
                            variant="primary"
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <UserManagementTableContainer
                  columns={columns}
                  data={usersList && usersList.length > 0 ? usersList : []}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setColValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  manualSorting={true}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search users..."
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default UserList;
